import React, { useEffect, useState } from "react";
import Tree from "react-d3-tree";
import axios from "axios";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  BiSolidSun,
  BiSolidMoon,
} from "react-icons/bi";
import {
  FaArrowLeft,
  FaSearch,
  FaRedo,
  FaExclamationTriangle,
  FaSpinner,
  FaCheckCircle,
  FaUsers,
  FaTimes,
} from "react-icons/fa";

const Cedvel = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const normalizeSide = (side) => {
    return String(side || "").trim().toLowerCase();
  };

  const [treeData, setTreeData] = useState(null);

  const [translate, setTranslate] = useState({
    x: window.innerWidth / 2,
    y: 150,
  });

  const [zoom, setZoom] = useState(0.9);

  const [allUsers, setAllUsers] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [isDarkMode, setIsDarkMode] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    return document.documentElement.classList.contains("dark");
  });

  /**
   * Serverdən gələn xətanı istifadəçiyə
   * mümkün qədər aydın şəkildə göstərmək üçün.
   */
  const getErrorMessage = (error, defaultMessage = "Naməlum xəta baş verdi.") => {
    if (!error) {
      return defaultMessage;
    }

    if (typeof error === "string") {
      return error;
    }

    const serverMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.data?.message ||
      error?.data?.error;

    if (serverMessage) {
      return serverMessage;
    }

    if (error?.message) {
      if (error.message === "Network Error") {
        return "Serverə qoşulmaq mümkün olmadı. İnternet bağlantınızı və serverin işlək vəziyyətdə olduğunu yoxlayın.";
      }

      return error.message;
    }

    switch (error?.response?.status || error?.status) {
      case 400:
        return "Göndərilən sorğu düzgün deyil. Zəhmət olmasa, məlumatları yoxlayın.";

      case 401:
        return "Bu məlumatları görmək üçün hesabınıza yenidən daxil olmağınız tələb oluna bilər.";

      case 403:
        return "Bu məlumatlara baxmaq üçün icazəniz yoxdur.";

      case 404:
        return "Axtarılan məlumat tapılmadı.";

      case 408:
        return "Serverdən cavab almaq çox uzun çəkdi. Zəhmət olmasa, yenidən cəhd edin.";

      case 429:
        return "Çox sayda sorğu göndərildi. Bir qədər gözləyib yenidən cəhd edin.";

      case 500:
        return "Server daxilində xəta baş verdi.";

      case 502:
        return "Server düzgün cavab qaytarmadı.";

      case 503:
        return "Server hazırda əlçatan deyil. Bir qədər sonra yenidən cəhd edin.";

      default:
        return defaultMessage;
    }
  };

  /**
   * Səhifənin ümumi sürüşdürülməsini bloklayırıq.
   * Ağacın özü hərəkət etdirilə bilər.
   */
  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.body.style.height = "100vh";

    return () => {
      document.body.style.overflow = "auto";
      document.body.style.height = "auto";
    };
  }, []);

  /**
   * Mövcud temanı izləyirik.
   */
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains("dark");

      setIsDarkMode(isDark);
      setDarkMode(isDark);
    };

    checkDarkMode();

    const observer = new MutationObserver(checkDarkMode);

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  /**
   * Saxlanılmış temanı tətbiq edirik.
   */
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");

    if (savedTheme === "dark") {
      document.documentElement.classList.add("dark");
      setDarkMode(true);
      setIsDarkMode(true);
    }

    if (savedTheme === "light") {
      document.documentElement.classList.remove("dark");
      setDarkMode(false);
      setIsDarkMode(false);
    }
  }, []);

  /**
   * Tema dəyişdirilir.
   */
  const toggleTheme = () => {
    const html = document.documentElement;

    if (html.classList.contains("dark")) {
      html.classList.remove("dark");

      localStorage.setItem("theme", "light");

      setDarkMode(false);
      setIsDarkMode(false);
    } else {
      html.classList.add("dark");

      localStorage.setItem("theme", "dark");

      setDarkMode(true);
      setIsDarkMode(true);
    }
  };

  /**
   * İstifadəçiləri serverdən gətiririk.
   */
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!userInfo) {
        if (isMounted) {
          setLoading(false);
          setErrorMessage(
            "İstifadəçi məlumatları tapılmadı. Zəhmət olmasa, hesabınıza daxil olun."
          );
        }

        return;
      }

      try {
        setLoading(true);
        setErrorMessage("");
        setSuccessMessage("");

        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

        if (!apiBaseUrl) {
          throw new Error(
            "API ünvanı müəyyən edilməyib. REACT_APP_API_BASE_URL dəyişənini yoxlayın."
          );
        }

        if (!userInfo.referralCode) {
          throw new Error(
            "Hesabınıza aid referans kodu tapılmadı. Referans ağacını yükləmək mümkün deyil."
          );
        }

        const [adminResult, userResult] = await Promise.allSettled([
          axios.get(
            `${apiBaseUrl}/api/users/admin/${userInfo.referralCode}`,
            {
              withCredentials: true,
            }
          ),

          axios.get(
            `${apiBaseUrl}/api/users/user/${userInfo.referralCode}`,
            {
              withCredentials: true,
            }
          ),
        ]);

        if (!isMounted) return;

        /**
         * Admin sorğusunun nəticəsi.
         */
        const adminUsers =
          adminResult.status === "fulfilled"
            ? adminResult.value?.data?.users || []
            : [];

        /**
         * Adi istifadəçi sorğusunun nəticəsi.
         */
        const userUsers =
          userResult.status === "fulfilled"
            ? userResult.value?.data?.users || []
            : [];

        /**
         * Hər iki nəticəni birləşdiririk.
         */
        const all = [...adminUsers, ...userUsers];

        const unique = {};

        all.forEach((user) => {
          if (user?._id && !unique[user._id]) {
            unique[user._id] = user;
          }
        });

        const users = Object.values(unique);

        /**
         * Əgər hər iki sorğu uğursuz olubsa,
         * istifadəçiyə səbəbi göstəririk.
         */
        if (
          adminResult.status === "rejected" &&
          userResult.status === "rejected"
        ) {
          const adminError = getErrorMessage(
            adminResult.reason,
            "Admin istifadəçilərini yükləmək mümkün olmadı."
          );

          const userError = getErrorMessage(
            userResult.reason,
            "İstifadəçiləri yükləmək mümkün olmadı."
          );

          throw new Error(
            `Məlumatlar yüklənmədi. Admin sorğusu: ${adminError} İstifadəçi sorğusu: ${userError}`
          );
        }

        setAllUsers(users);

        /**
         * Ağac üçün əsas istifadəçini qururuq.
         */
        const rootUser = selectedUser || userInfo;

        buildTree(rootUser, users);

        setSuccessMessage(
          `${users.length} istifadəçinin məlumatı uğurla yükləndi.`
        );

        setTimeout(() => {
          if (isMounted) {
            setSuccessMessage("");
          }
        }, 3000);
      } catch (err) {
        if (!isMounted) return;

        console.error(
          "Referans ağacı yüklənə bilmədi:",
          err
        );

        setTreeData(null);

        setErrorMessage(
          getErrorMessage(
            err,
            "Referans ağacı yüklənərkən gözlənilməyən xəta baş verdi."
          )
        );
      } finally {
        if (isMounted) {
          setLoading(false);
          setSearchLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [userInfo, selectedUser]);

  /**
   * Binary ağacı qururuq.
   */
  const buildTree = (rootUser, users) => {
    const assigned = new Set();

    const buildBinaryTree = (parent) => {
      if (
        !parent ||
        !parent._id ||
        assigned.has(parent._id)
      ) {
        return null;
      }

      assigned.add(parent._id);

      const children = users.filter(
        (user) => user.referredBy === parent.referralCode
      );

      const left = children.find(
        (user) =>
          normalizeSide(user.side) === "left"
      );

      const right = children.find(
        (user) =>
          normalizeSide(user.side) === "right"
      );

      const node = {
        name: parent.name,
        attributes: {
          Kod: parent.referralCode,
          Ödəniş: parent.payment,
          Qol: normalizeSide(parent.side),
          Photo:
            parent.photo ||
            parent.avatar ||
            "",
        },
      };

      const childNodes = [];

      if (left) {
        const leftNode = buildBinaryTree(left);

        if (leftNode) {
          childNodes.push(leftNode);
        }
      }

      if (right) {
        const rightNode = buildBinaryTree(right);

        if (rightNode) {
          childNodes.push(rightNode);
        }
      }

      if (childNodes.length > 0) {
        node.children = childNodes;
      }

      return node;
    };

    const rootTree = buildBinaryTree(rootUser);

    setTreeData(rootTree);
  };

  /**
   * Axtarış sahəsində yazılan mətn.
   */
  const handleSearchInputChange = (e) => {
    const value = e.target.value;

    setSearchInput(value);
    setErrorMessage("");

    if (!value.trim()) {
      setSuggestions([]);
      return;
    }

    const searchValue = value.toLowerCase();

    const filtered = allUsers.filter((user) => {
      const name = String(
        user.name || ""
      ).toLowerCase();

      const referralCode = String(
        user.referralCode || ""
      ).toLowerCase();

      return (
        name.includes(searchValue) ||
        referralCode.includes(searchValue)
      );
    });

    setSuggestions(filtered.slice(0, 10));
  };

  /**
   * Təkliflərdən istifadəçi seçilir.
   */
  const handleSuggestionClick = (user) => {
    setSearchInput(user.name || user.referralCode || "");
    setSuggestions([]);
    setErrorMessage("");
  };

  /**
   * İstifadəçi axtarışı.
   */
  const handleSearch = () => {
    const input = searchInput.trim().toLowerCase();

    if (!input) {
      setErrorMessage(
        "Axtarış üçün istifadəçinin adını və ya referans kodunu daxil edin."
      );

      return;
    }

    setSearchLoading(true);
    setErrorMessage("");

    const found = allUsers.find((user) => {
      const name = String(
        user.name || ""
      ).toLowerCase();

      const referralCode = String(
        user.referralCode || ""
      ).toLowerCase();

      return (
        name === input ||
        referralCode === input
      );
    });

    if (found) {
      setLoading(true);
      setSelectedUser(found);
      setSuggestions([]);

      setSuccessMessage(
        `"${found.name}" istifadəçisinin referans ağacı göstərilir.`
      );

      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    } else {
      setSearchLoading(false);

      setErrorMessage(
        `"${searchInput}" üzrə istifadəçi tapılmadı. İstifadəçinin adını və ya referans kodunu düzgün yazdığınızdan əmin olun.`
      );

      setSuggestions([]);
    }
  };

  /**
   * Ağacı ilkin vəziyyətə qaytarır.
   */
  const handleReset = () => {
    setLoading(true);
    setSearchLoading(false);

    setSelectedUser(null);
    setSearchInput("");
    setSuggestions([]);

    setErrorMessage("");
    setSuccessMessage("");

    setZoom(0.9);

    setTranslate({
      x: window.innerWidth / 2,
      y: 150,
    });
  };

  /**
   * Profil səhifəsinə qayıdır.
   */
  const handleBackClick = () => {
    navigate("/profile");
  };

  /**
   * Ağac düyünlərinin görünüşü.
   */
  const renderCustomNode = ({ nodeDatum }) => {
    const isPaid = Boolean(
      nodeDatum.attributes?.Ödəniş
    );

    const paymentStatusText = isPaid
      ? "Ödəniş edilib"
      : "Ödəniş edilməyib";

    const userPhoto =
      nodeDatum.attributes?.Photo;

    const cardBg = isDarkMode
      ? "#1E293B"
      : "#FFFFFF";

    const borderColor = isDarkMode
      ? "#334155"
      : "#E2E8F0";

    const textColorPrimary = isDarkMode
      ? "#F8FAFC"
      : "#0F172A";

    const textColorSecondary = isDarkMode
      ? "#94A3B8"
      : "#64748B";

    const statusBg = isPaid
      ? "rgba(16, 185, 129, 0.15)"
      : "rgba(239, 68, 68, 0.15)";

    const statusText = isPaid
      ? "#10B981"
      : "#EF4444";

    const rectWidth = Math.max(
      220,
      String(nodeDatum.name || "").length * 11
    );

    const rectHeight = 96;
    const rectX = -(rectWidth / 2);

    return (
      <g className="cursor-pointer transition-all duration-300 hover:scale-105">
        <rect
          width={rectWidth}
          height={rectHeight}
          x={rectX}
          y="-48"
          fill={cardBg}
          stroke={borderColor}
          strokeWidth="1.5"
          rx="16"
          style={{
            filter: isDarkMode
              ? "drop-shadow(0 10px 8px rgba(0,0,0,0.5))"
              : "drop-shadow(0 10px 8px rgba(0,0,0,0.06))",
          }}
        />

        <foreignObject
          x={rectX}
          y="-48"
          width={rectWidth}
          height={rectHeight}
          style={{
            overflow: "visible",
          }}
        >
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            className="flex h-full w-full items-center gap-3 p-3 font-sans"
          >
            {/* PROFILE PHOTO */}
            <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-indigo-50 dark:border-slate-600 dark:bg-slate-700">
              {userPhoto ? (
                <img
                  src={userPhoto}
                  alt={nodeDatum.name || "İstifadəçi"}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                  {nodeDatum.name
                    ?.charAt(0)
                    ?.toUpperCase() || "İ"}
                </span>
              )}
            </div>

            {/* USER INFO */}
            <div className="flex min-w-0 flex-1 flex-col justify-center text-left">
              <div
                className="truncate text-sm font-bold tracking-wide"
                style={{
                  color: textColorPrimary,
                }}
              >
                {nodeDatum.name ||
                  "Adsız istifadəçi"}
              </div>

              <div
                className="mb-1 text-[11px]"
                style={{
                  color: textColorSecondary,
                }}
              >
                Referans kodu:{" "}
                <strong
                  style={{
                    color: textColorPrimary,
                  }}
                >
                  {nodeDatum.attributes?.Kod ||
                    "-"}
                </strong>
              </div>

              <div
                className="self-start rounded-full px-2 py-0.5 text-[9px] font-semibold tracking-wide"
                style={{
                  backgroundColor: statusBg,
                  color: statusText,
                }}
              >
                {paymentStatusText}
              </div>
            </div>
          </div>
        </foreignObject>
      </g>
    );
  };

  return (
    <div className="fixed inset-0 h-screen w-screen select-none overflow-hidden bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      {/* HEADER */}
      <header className="fixed left-0 right-0 top-0 z-50 min-h-[88px] border-b border-slate-200 bg-white/95 px-3 py-3 shadow-lg backdrop-blur-xl dark:border-slate-800 dark:bg-slate-900/95">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* LEFT */}
          <div className="flex min-w-0 items-center gap-2">
            <button
              onClick={handleBackClick}
              className="flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-200 active:scale-95 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 sm:text-sm"
            >
              <FaArrowLeft />
              <span>Geri qayıt</span>
            </button>

            <div className="hidden h-8 w-px bg-slate-200 dark:bg-slate-700 sm:block" />

            <div className="flex min-w-0 items-center gap-2">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md">
                <FaUsers />
              </div>

              <div className="min-w-0">
                <h2 className="truncate text-sm font-bold tracking-tight text-slate-900 dark:text-white sm:text-base">
                  Referans ağacı
                </h2>

                <p className="hidden text-[10px] text-slate-500 dark:text-slate-400 sm:block">
                  Binary sistem üzrə istifadəçi ağacı
                </p>
              </div>
            </div>

            <span className="hidden rounded-full bg-indigo-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400 md:inline-flex">
              Binary
            </span>

            {/* THEME */}
            <button
              type="button"
              onClick={toggleTheme}
              aria-label="Temanı dəyiş"
              title="Temanı dəyiş"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600 transition hover:scale-[1.03] hover:bg-slate-200 dark:bg-white/[0.06] dark:text-slate-300 dark:hover:bg-white/10 sm:h-11 sm:w-11"
            >
              {darkMode ? (
                <BiSolidSun
                  size={20}
                  className="text-amber-400"
                />
              ) : (
                <BiSolidMoon size={20} />
              )}
            </button>
          </div>

          {/* RIGHT / SEARCH */}
          <div className="flex w-full items-center gap-2 sm:w-auto sm:min-w-[360px] sm:max-w-lg">
            <div className="relative flex-1">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400" />

              <input
                type="text"
                value={searchInput}
                onChange={handleSearchInputChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
                placeholder="Ad və ya referans kodu ilə axtar..."
                className="h-10 w-full rounded-xl border border-slate-200 bg-slate-100 pl-9 pr-3 text-xs text-slate-900 outline-none transition focus:border-indigo-500 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:bg-slate-900 sm:h-11 sm:text-sm"
              />

              {/* SUGGESTIONS */}
              {suggestions.length > 0 && (
                <ul className="absolute left-0 right-0 top-full z-[100] mt-2 max-h-64 overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-800">
                  {suggestions.map((user) => (
                    <li
                      key={user._id}
                      onClick={() =>
                        handleSuggestionClick(user)
                      }
                      className="flex cursor-pointer items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 text-xs transition last:border-b-0 hover:bg-indigo-50 dark:border-slate-700 dark:hover:bg-slate-700"
                    >
                      <span className="min-w-0 truncate font-semibold text-slate-700 dark:text-slate-200">
                        {user.name ||
                          "Adsız istifadəçi"}
                      </span>

                      <span className="shrink-0 rounded-lg bg-slate-100 px-2 py-1 font-mono text-[10px] text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                        {user.referralCode ||
                          "-"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* SEARCH BUTTON */}
            <button
              onClick={handleSearch}
              disabled={searchLoading}
              className="flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 text-xs font-bold text-white shadow-md transition hover:from-indigo-700 hover:to-violet-700 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 sm:h-11 sm:text-sm"
            >
              {searchLoading ? (
                <FaSpinner className="animate-spin" />
              ) : (
                <FaSearch />
              )}

              <span className="hidden sm:inline">
                Axtar
              </span>
            </button>

            {/* RESET */}
            <button
              onClick={handleReset}
              disabled={loading}
              title="Ağacı sıfırla"
              className="flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl bg-amber-500 px-3 text-xs font-bold text-white shadow-md transition hover:bg-amber-600 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 sm:h-11 sm:px-4 sm:text-sm"
            >
              <FaRedo
                className={
                  loading
                    ? "animate-spin"
                    : ""
                }
              />

              <span className="hidden sm:inline">
                Sıfırla
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* SUCCESS MESSAGE */}
      {successMessage && (
        <div className="fixed left-1/2 top-[98px] z-[70] w-[calc(100%-24px)] max-w-lg -translate-x-1/2">
          <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 shadow-xl dark:border-emerald-900/50 dark:bg-emerald-950/50">
            <FaCheckCircle className="mt-0.5 shrink-0 text-emerald-600" />

            <div className="min-w-0 flex-1">
              <p className="font-semibold text-emerald-800 dark:text-emerald-300">
                Əməliyyat uğurla tamamlandı
              </p>

              <p className="mt-1 break-words text-xs leading-5 text-emerald-700 dark:text-emerald-400">
                {successMessage}
              </p>
            </div>

            <button
              onClick={() =>
                setSuccessMessage("")
              }
              className="text-emerald-600"
              aria-label="Bildirişi bağla"
            >
              <FaTimes />
            </button>
          </div>
        </div>
      )}

      {/* ERROR MESSAGE */}
      {errorMessage && (
        <div className="fixed left-1/2 top-[98px] z-[70] w-[calc(100%-24px)] max-w-2xl -translate-x-1/2">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 shadow-xl dark:border-red-900/50 dark:bg-red-950/50">
            <div className="flex items-start gap-3">
              <FaExclamationTriangle className="mt-0.5 shrink-0 text-red-600" />

              <div className="min-w-0 flex-1">
                <p className="font-semibold text-red-800 dark:text-red-300">
                  Xəta baş verdi
                </p>

                <p className="mt-1 break-words text-xs leading-5 text-red-700 dark:text-red-400">
                  {errorMessage}
                </p>
              </div>

              <button
                onClick={() =>
                  setErrorMessage("")
                }
                className="shrink-0 text-red-600"
                aria-label="Xətanı bağla"
              >
                <FaTimes />
              </button>
            </div>

            <button
              onClick={handleReset}
              className="mt-3 inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-red-700"
            >
              <FaRedo />
              Yenidən cəhd edin
            </button>
          </div>
        </div>
      )}

      {/* MAIN TREE AREA */}
      <main className="relative h-full w-full cursor-grab overflow-hidden pt-[88px] active:cursor-grabbing">
        {loading ? (
          <div className="flex h-full w-full items-center justify-center px-5">
            <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-xl dark:border-slate-800 dark:bg-slate-900">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-indigo-50 dark:bg-indigo-950">
                <FaSpinner className="animate-spin text-2xl text-indigo-600" />
              </div>

              <h2 className="mt-5 text-xl font-bold text-slate-900 dark:text-white">
                Referans ağacı yüklənir...
              </h2>

              <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                İstifadəçilər və onların əlaqələri hazırlanır.
                Zəhmət olmasa, gözləyin.
              </p>

              <div className="mx-auto mt-5 h-2 max-w-xs overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                <div className="h-full w-1/2 animate-pulse rounded-full bg-gradient-to-r from-indigo-500 to-violet-600" />
              </div>
            </div>
          </div>
        ) : treeData ? (
          <Tree
            data={treeData}
            orientation="vertical"
            translate={translate}
            zoom={zoom}
            collapsible={false}
            pathFunc="step"
            separation={{
              siblings: 2.5,
              nonSiblings: 3,
            }}
            renderCustomNodeElement={
              renderCustomNode
            }
            zoomable={true}
            draggable={true}
            pathClassFunc={() =>
              isDarkMode
                ? "!stroke-slate-600 !stroke-[1.5px]"
                : "!stroke-slate-300 !stroke-[1.5px]"
            }
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center px-5">
            <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-xl dark:border-slate-800 dark:bg-slate-900">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
                <FaUsers className="text-2xl text-slate-400" />
              </div>

              <h2 className="mt-5 text-xl font-bold text-slate-900 dark:text-white">
                Referans ağacı göstərilə bilmir
              </h2>

              <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                Ağacı göstərmək üçün lazımi istifadəçi
                məlumatları mövcud deyil.
              </p>

              <button
                onClick={handleReset}
                className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:scale-[1.02]"
              >
                <FaRedo />
                Yenidən cəhd edin
              </button>
            </div>
          </div>
        )}

        {/* ZOOM CONTROLS */}
        <div className="fixed bottom-6 right-5 z-40 flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white/95 shadow-xl backdrop-blur dark:border-slate-700 dark:bg-slate-800/95">
          <button
            onClick={() =>
              setZoom((prev) =>
                Math.min(prev + 0.2, 2.5)
              )
            }
            className="flex h-10 w-10 items-center justify-center text-xl font-bold text-slate-600 transition hover:bg-indigo-50 hover:text-indigo-600 dark:text-slate-300 dark:hover:bg-slate-700"
            title="Yaxınlaşdır"
            aria-label="Yaxınlaşdır"
          >
            +
          </button>

          <div className="h-px w-full bg-slate-200 dark:bg-slate-700" />

          <div className="flex h-8 items-center justify-center px-2 text-[10px] font-semibold text-slate-400">
            {Math.round(zoom * 100)}%
          </div>

          <div className="h-px w-full bg-slate-200 dark:bg-slate-700" />

          <button
            onClick={() =>
              setZoom((prev) =>
                Math.max(prev - 0.2, 0.3)
              )
            }
            className="flex h-10 w-10 items-center justify-center text-xl font-bold text-slate-600 transition hover:bg-indigo-50 hover:text-indigo-600 dark:text-slate-300 dark:hover:bg-slate-700"
            title="Uzaqlaşdır"
            aria-label="Uzaqlaşdır"
          >
            −
          </button>
        </div>

        {/* USER COUNT */}
        {!loading && allUsers.length > 0 && (
          <div className="fixed bottom-6 left-5 z-40 rounded-2xl border border-slate-200 bg-white/95 px-4 py-3 shadow-xl backdrop-blur dark:border-slate-700 dark:bg-slate-800/95">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400">
                <FaUsers />
              </div>

              <div>
                <p className="text-[10px] text-slate-400">
                  İstifadəçi sayı
                </p>

                <p className="text-sm font-bold text-slate-800 dark:text-white">
                  {allUsers.length}
                </p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Cedvel;
